import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import axios from 'axios'
import swal from 'sweetalert2'
import { Usercntxt } from '../../context/UserContext'
import { CFormSelect } from '@coreui/react'
import TableRow from './TableRow'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { PaginationBar, SearchBar } from 'src/components'
import { parseISO, format as formatDate } from 'date-fns'
import {
  clearXeroContactsCache,
  updateXeroContactsCache,
  getCachedXeroContacts,
} from '../../services/xeroContactsCache'

/* ============================= Constants ============================= */

const LIST_URL = '/api/show_invoices_duplicate'
const SEARCH_STATUS_URL = '/api/search_status'
const SEARCH_DEBOUNCE_MS = 500
const CACHE_TTL_MS = 10 * 60 * 60 * 1000 // 10 hours
const CACHE_MAX_ITEMS = 200
const BACKGROUND_CACHE_DELAY_MS = 100

const ORDERS_CACHE_MUTATION_KEY = 'orders_cache_mutation'
const ORDERS_CACHE_CHANNEL = 'orders_cache_channel'
const ORDERS_CACHE_METADATA_KEY = 'orders_cache_metadata'

const ORDERS_SSE_CHANNEL = 'orders_sse_channel'
const ORDERS_SSE_MESSAGE_KEY = 'orders_sse_message'
const ORDERS_SSE_VERSION_KEY = 'orders_sse_version'
const ORDERS_SSE_LEADER_KEY = 'orders_sse_leader'
const ORDERS_SSE_LEADER_TTL = 15000

/* ============================= IndexedDB Cache ============================= */

const DB_NAME = 'OrdersCacheDB'
const STORE_NAME = 'orderPages'
const DB_VERSION = 1

let dbInstance = null

async function initDB() {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance)
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.error('[Orders Cache] IndexedDB failed to open')
      reject(request.error)
    }

    request.onsuccess = () => {
      dbInstance = request.result
      console.log('[Orders Cache] ✅ IndexedDB initialized')
      resolve(dbInstance)
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'url' })
        console.log('[Orders Cache] IndexedDB schema created')
      }
    }
  })
}

async function getFromIndexedDB(url) {
  try {
    if (!dbInstance) await initDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(url)

      request.onsuccess = () => {
        const cached = request.result

        if (!cached) {
          resolve(null)
          return
        }

        const cacheAge = Date.now() - cached.timestamp
        const isValid = cacheAge < CACHE_TTL_MS

        if (isValid) {
          const ageMinutes = Math.round(cacheAge / 60000)
          const remainingHours = ((CACHE_TTL_MS - cacheAge) / (60 * 60 * 1000)).toFixed(1)

          console.log(
            `[Orders Cache] IndexedDB HIT (${ageMinutes}min old, expires in ${remainingHours}h)`,
          )

          resolve(cached.data)
        } else {
          console.log('[Orders Cache] IndexedDB data EXPIRED')
          resolve(null)
        }
      }

      request.onerror = () => resolve(null)
    })
  } catch (error) {
    console.error('[Orders Cache] IndexedDB read failed:', error)
    return null
  }
}

async function getStaleFromIndexedDB(url) {
  const record = await getIndexedDBRecord(url)
  return record?.data || null
}

async function saveToIndexedDB(url, data) {
  try {
    if (!dbInstance) await initDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const cacheEntry = {
        url,
        data,
        timestamp: Date.now(),
        expiresAt: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
      }

      const request = store.put(cacheEntry)

      request.onsuccess = () => {
        console.log(`[Orders Cache] ✅ Stored in IndexedDB: ${url}`)
        resolve(true)
      }

      request.onerror = () => {
        console.error('[Orders Cache] IndexedDB write failed')
        resolve(false)
      }
    })
  } catch (error) {
    console.error('[Orders Cache] Failed to save to IndexedDB:', error)
    return false
  }
}

async function getIndexedDBRecord(url) {
  try {
    if (!dbInstance) await initDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(url)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        resolve(null)
      }
    })
  } catch (error) {
    console.error('[Orders Cache] Failed to read IndexedDB record:', error)
    return null
  }
}

async function saveIndexedDBRecord(record) {
  try {
    if (!dbInstance) await initDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(record)

      request.onsuccess = () => resolve(true)
      request.onerror = () => resolve(false)
    })
  } catch (error) {
    console.error('[Orders Cache] Failed to save IndexedDB record:', error)
    return false
  }
}

async function clearIndexedDBPages() {
  try {
    if (!dbInstance) await initDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => resolve(true)
      request.onerror = () => resolve(false)
    })
  } catch (error) {
    console.error('[Orders Cache] Failed to clear IndexedDB:', error)
    return false
  }
}

/* ============================= Helpers ============================= */

function sortOrdersByNumber(orders) {
  return [...orders].sort((first, second) => {
    const firstNumber = Number(first?.id)
    const secondNumber = Number(second?.id)

    if (Number.isFinite(firstNumber) && Number.isFinite(secondNumber)) {
      return secondNumber - firstNumber
    }

    return String(second?.id ?? '').localeCompare(String(first?.id ?? ''))
  })
}

function buildApiUrl(page, { status, search = '' }) {
  const cleanStatus = String(status ?? '').trim()

  const params = new URLSearchParams({ page: String(page) })

  const hasServerFilter = Boolean(cleanStatus)

  if (cleanStatus) {
    params.set('status', cleanStatus)
  }

  return `${hasServerFilter ? SEARCH_STATUS_URL : LIST_URL}?${params.toString()}`
}

function loadPersistentCacheMetadata() {
  try {
    const cached = localStorage.getItem(ORDERS_CACHE_METADATA_KEY)

    if (!cached) {
      console.log('[Orders Cache] ❌ No metadata in localStorage')
      return null
    }

    const metadata = JSON.parse(cached)

    if (!metadata) {
      console.log('[Orders Cache] ❌ Metadata is null')
      return null
    }

    const cacheAge = Date.now() - metadata.timestamp
    const isValid = cacheAge < CACHE_TTL_MS
    const ageMinutes = Math.round(cacheAge / 60000)
    const remainingHours = ((CACHE_TTL_MS - cacheAge) / (60 * 60 * 1000)).toFixed(1)

    if (isValid) {
      console.log(
        `[Orders Cache] ✅ Metadata valid (${ageMinutes}min old, expires in ${remainingHours}h, pages: ${
          metadata.pagesCached?.length || 0
        })`,
      )

      return metadata
    }

    console.log('[Orders Cache] ⏰ Metadata expired')
    localStorage.removeItem(ORDERS_CACHE_METADATA_KEY)
    return null
  } catch (error) {
    console.error('[Orders Cache] Failed to load cache metadata:', error)
    return null
  }
}

function savePersistentCacheMetadata(metadata) {
  try {
    const data = {
      lastPage: metadata.lastPage,
      totalRecords: metadata.totalRecords,
      pagesCached: metadata.pagesCached,
      timestamp: Date.now(),
      expiresAt: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
    }

    localStorage.setItem(ORDERS_CACHE_METADATA_KEY, JSON.stringify(data))

    console.log(`[Orders Cache] ✅ Saved metadata - ${data.pagesCached?.length || 0} pages cached`)
  } catch (error) {
    console.error('[Orders Cache] Failed to save cache metadata:', error)
  }
}

async function loadCachedPagesFromIndexedDB(pageNumbers) {
  try {
    if (!dbInstance) await initDB()

    const allRecords = []

    for (const page of pageNumbers) {
      const url = buildApiUrl(page, { status: '', search: '' })
      const cachedData = await getFromIndexedDB(url)

      if (cachedData && cachedData.invoice?.data) {
        allRecords.push(...cachedData.invoice.data)
      }
    }

    console.log(
      `[Orders Cache] ✅ Loaded ${allRecords.length} records from ${pageNumbers.length} cached pages`,
    )

    return allRecords
  } catch (error) {
    console.error('[Orders Cache] Failed to load pages from IndexedDB:', error)
    return []
  }
}

async function patchOrderInsideIndexedDBPages(orderId, patcher) {
  try {
    const metadata = loadPersistentCacheMetadata()
    const pages = metadata?.pagesCached || []

    if (!pages.length) return false

    let changed = false

    for (const page of pages) {
      const url = buildApiUrl(page, { status: '', search: '' })
      const record = await getIndexedDBRecord(url)

      if (!record?.data?.invoice?.data || !Array.isArray(record.data.invoice.data)) {
        continue
      }

      const oldRows = record.data.invoice.data
      let pageChanged = false

      const newRows = oldRows
        .map((row) => {
          if (Number(row.id) !== Number(orderId)) return row

          pageChanged = true
          changed = true

          return patcher(row)
        })
        .filter(Boolean)

      if (pageChanged) {
        record.data.invoice.data = newRows
        record.timestamp = Date.now()
        record.expiresAt = new Date(Date.now() + CACHE_TTL_MS).toISOString()

        await saveIndexedDBRecord(record)

        console.log(`[Orders Cache] ✅ Patched IndexedDB page ${page} for order ${orderId}`)
      }
    }

    return changed
  } catch (error) {
    console.error('[Orders Cache] Failed to patch IndexedDB pages:', error)
    return false
  }
}

async function insertOrderIntoIndexedDBPageOne(order) {
  try {
    const url = buildApiUrl(1, { status: '', search: '' })
    const record = await getIndexedDBRecord(url)

    if (!record?.data?.invoice?.data || !Array.isArray(record.data.invoice.data)) {
      return false
    }

    const rows = record.data.invoice.data
    const exists = rows.some((row) => Number(row.id) === Number(order.id))

    if (!exists) {
      rows.unshift(order)

      const perPage = record.data.invoice.per_page || 100
      record.data.invoice.data = rows.slice(0, perPage)

      if (record.data.invoice.total !== undefined) {
        record.data.invoice.total = Number(record.data.invoice.total || 0) + 1
      }

      record.timestamp = Date.now()
      record.expiresAt = new Date(Date.now() + CACHE_TTL_MS).toISOString()

      await saveIndexedDBRecord(record)

      console.log(`[Orders Cache] ✅ Inserted order ${order.id} into IndexedDB page 1`)
    }

    return true
  } catch (error) {
    console.error('[Orders Cache] Failed to insert order into IndexedDB page 1:', error)
    return false
  }
}

function normalize(text) {
  return String(text ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function toNumber(value) {
  if (value === true) return 1
  if (value === false) return 0
  if (value === null || value === undefined || value === '') return 0

  const n = Number(value)

  return Number.isNaN(n) ? 0 : n
}

function deepValues(val, out = []) {
  if (val == null) return out

  const t = typeof val

  if (t === 'string' || t === 'number' || t === 'boolean') {
    out.push(String(val))
    return out
  }

  if (Array.isArray(val)) {
    for (const v of val) deepValues(v, out)
    return out
  }

  if (t === 'object') {
    for (const [k, v] of Object.entries(val)) {
      out.push(String(k))
      deepValues(v, out)
    }

    return out
  }

  return out
}

function collectProductKeywords(item) {
  const out = []
  const lists = item?.order_list ?? []

  for (const list of lists) {
    for (const p of list?.order_list_products ?? []) {
      if (!p) continue
      ;[
        p.product_name,
        p.product_code,
        p.code,
        p.sku,
        p.sku_code,
        p.item_code,
        p?.product?.name,
        p?.product?.code,
        p?.product?.sku,
      ].forEach((v) => {
        if (v) out.push(String(v))
      })
    }
  }

  return out
}

function collectDocketKeywords(item) {
  const out = []
  const lists = item?.order_list ?? []

  for (const list of lists) {
    for (const docket of list?.docket_numbers ?? []) {
      if (!docket) continue
      ;[
        docket.docket_number,
        docket.docket_date,
        docket.sku_location,
        docket.weight,
        docket.imageName,
      ].forEach((v) => {
        if (v) out.push(String(v))
      })
    }
  }

  return out
}

function collectLocalRelationshipKeywords(item) {
  const out = []

  if (item?.orderdeliveryzone?.name) out.push(item.orderdeliveryzone.name)

  if (item?.machine?.name) out.push(item.machine.name)
  if (item?.machine?.machine_code) out.push(item.machine.machine_code)

  if (item?.operator?.fname) out.push(item.operator.fname)
  if (item?.operator?.lname) out.push(item.operator.lname)

  if (item?.operator?.fname || item?.operator?.lname) {
    out.push(`${item?.operator?.fname ?? ''} ${item?.operator?.lname ?? ''}`)
  }

  if (Array.isArray(item?.operators)) {
    for (const op of item.operators) {
      if (op?.fname) out.push(op.fname)
      if (op?.lname) out.push(op.lname)

      if (op?.fname || op?.lname) {
        out.push(`${op?.fname ?? ''} ${op?.lname ?? ''}`)
      }
    }
  }

  if (Array.isArray(item?.order_operators)) {
    for (const row of item.order_operators) {
      if (row?.operator?.fname) out.push(row.operator.fname)
      if (row?.operator?.lname) out.push(row.operator.lname)

      if (row?.operator?.fname || row?.operator?.lname) {
        out.push(`${row?.operator?.fname ?? ''} ${row?.operator?.lname ?? ''}`)
      }
    }
  }

  return out
}

function formatDateVariants(d) {
  try {
    const dt = typeof d === 'string' ? parseISO(d) : new Date(d)

    if (isNaN(dt)) return []

    return [
      formatDate(dt, 'dd/MM/yyyy'),
      formatDate(dt, 'dd/MM/yyyy HH:mm:ss'),
      formatDate(dt, 'dd/MM/yyyy | HH:mm:ss'),
      formatDate(dt, 'yyyy-MM-dd'),
      formatDate(dt, 'yyyy-MM-dd HH:mm:ss'),
    ]
  } catch {
    return []
  }
}

function collectDateKeywords(item) {
  const out = []

  if (item?.updated_at) out.push(...formatDateVariants(item.updated_at))
  if (item?.created_at) out.push(...formatDateVariants(item.created_at))

  const stack = [item]

  while (stack.length) {
    const v = stack.pop()

    if (v == null) continue

    if (typeof v === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
        out.push(...formatDateVariants(v))
      }

      continue
    }

    if (Array.isArray(v)) {
      for (const x of v) stack.push(x)
      continue
    }

    if (typeof v === 'object') {
      for (const k in v) stack.push(v[k])
    }
  }

  return out
}

function toSearchHaystack(row) {
  const pieces = []

  pieces.push(...deepValues(row))
  pieces.push(...collectProductKeywords(row))
  pieces.push(...collectDocketKeywords(row))
  pieces.push(...collectLocalRelationshipKeywords(row))
  pieces.push(...collectDateKeywords(row))

  return normalize(pieces.join(' '))
}

function tokensFrom(text) {
  return normalize(text).split(' ').filter(Boolean)
}

function getOrdersTabId() {
  let tabId = sessionStorage.getItem('orders_tab_id')

  if (!tabId) {
    tabId = `${Date.now()}_${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('orders_tab_id', tabId)
  }

  return tabId
}

function getStoredAuthToken() {
  const fromAxios =
    axios.defaults.headers.common.Authorization || axios.defaults.headers.common.authorization || ''

  const tokenFromAxios = String(fromAxios)
    .replace(/^Bearer\s+/i, '')
    .trim()

  return (
    tokenFromAxios ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('sanctum_token') ||
    localStorage.getItem('user_token') ||
    sessionStorage.getItem('auth_token') ||
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('access_token') ||
    sessionStorage.getItem('sanctum_token') ||
    ''
  )
}

function getBackendRootUrl() {
  const envUrl = process.env.REACT_APP_URL || ''
  const axiosBaseUrl = axios.defaults.baseURL || ''

  // Your .env REACT_APP_URL points to Laravel root:
  // https://staging.mwlime.dblq.io/
  let baseUrl = String(envUrl || axiosBaseUrl || '').trim()

  if (!baseUrl) {
    return ''
  }

  baseUrl = baseUrl.replace(/\/+$/, '')

  // If someone sets REACT_APP_URL with /api at the end, remove it here
  // because buildSseUrl() adds /api/orders-events.
  if (baseUrl.endsWith('/api')) {
    baseUrl = baseUrl.slice(0, -4)
  }

  return baseUrl
}

function buildSseUrl(lastVersion) {
  const backendRootUrl = getBackendRootUrl()
  const token = getStoredAuthToken()

  const params = new URLSearchParams({
    version: String(lastVersion || '0'),
  })

  if (token) {
    params.set('token', token)
  }

  if (backendRootUrl) {
    return `${backendRootUrl}/api/orders-events?${params.toString()}`
  }

  return `/api/orders-events?${params.toString()}`
}

function maskSseUrlForLog(url) {
  try {
    const parsed = new URL(url, window.location.origin)

    if (parsed.searchParams.has('token')) {
      parsed.searchParams.set('token', '***')
    }

    return parsed.toString()
  } catch {
    return String(url || '').replace(/token=[^&]+/i, 'token=***')
  }
}

function extractDisplayFields(item) {
  if (!item) return item

  return {
    id: item.id,

    order_status: item.order_status,
    dockets_ready: item.dockets_ready,
    dockets_complete: item.dockets_complete,
    tons_complete: item.tons_complete,
    is_xero: item.is_xero,

    created_at: item.created_at,
    updated_at: item.updated_at,
    tons_ordered: item.tons_ordered,
    tons_actual: item.tons_actual,
    machine_id: item.machine_id,
    order_note: item.order_note,

    customer: item.customer
      ? {
          code: item.customer.code,
          account_name: item.customer.account_name,
          contact_id: item.customer.contact_id,
          customer_address: item.customer.customer_address,
        }
      : null,

    customer_address: item.customer_address,
    order_list: item.order_list,

    orderdeliveryzone: item.orderdeliveryzone
      ? {
          name: item.orderdeliveryzone.name,
        }
      : null,

    machine: item.machine
      ? {
          name: item.machine.name,
          machine_code: item.machine.machine_code,
        }
      : null,

    operator: item.operator,
    order_operators: item.order_operators,
    operators: item.operators,
  }
}

/* ============================= Component ============================= */

function Open() {
  const user = useContext(Usercntxt)

  const [rows, setRows] = useState([])
  const [xeroContacts, setXeroContacts] = useState([])

  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [serverSearch, setServerSearch] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [pageFilter, setPageFilter] = useState('')
  const [cachePercentage, setCachePercentage] = useState(0)
  const [isCacheReady, setIsCacheReady] = useState(false)

  const [pg, setPg] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 100,
    total: 0,
    from: 0,
    to: 0,
  })

  const reqSeqRef = useRef(0)
  const searchDebounceRef = useRef(null)
  const abortControllerRef = useRef(null)
  const responseCacheRef = useRef(new Map())

  const allPagesDataRef = useRef([])
  const pagesCachedRef = useRef(new Set())
  const isCachingRef = useRef(false)
  const cacheTimeoutRef = useRef(null)

  const cachedSearchResultsRef = useRef(null)
  const lastAppliedSseVersionRef = useRef(Number(localStorage.getItem(ORDERS_SSE_VERSION_KEY) || 0))

  useEffect(() => {
    if (user?.id) {
      window.__currentUserId = user.id
      localStorage.setItem('current_user_id', String(user.id))
    }
  }, [user?.id])

  function applyOrdersResponse(data) {
    const invoice = data.invoice ?? {}
    const invoiceData = Array.isArray(invoice.data) ? invoice.data : []
    const extractedData = invoiceData.map(extractDisplayFields)

    setRows(extractedData)

    if ('xero_contacts' in data) {
      const xeroData = data.xero_contacts ?? []

      setXeroContacts(xeroData)

      if (xeroData && Object.keys(xeroData).length > 0) {
        updateXeroContactsCache(xeroData).catch((err) => {
          console.debug('[Xero Contacts] Failed to cache:', err.message)
        })
      }
    }

    const newPg = {
      current_page: invoice.current_page ?? 1,
      last_page: invoice.last_page ?? 1,
      per_page: invoice.per_page ?? 100,
      total: invoice.total ?? 0,
      from: invoice.from ?? 0,
      to: invoice.to ?? 0,
    }

    setPg(newPg)

    if (newPg.current_page === 1 && !statusFilter && !serverSearch) {
      allPagesDataRef.current = [...extractedData]
      pagesCachedRef.current.clear()
      pagesCachedRef.current.add(1)

      if (newPg.last_page <= 1) {
        cachedSearchResultsRef.current = [...extractedData]
        setCachePercentage(100)
        setIsCacheReady(true)

        savePersistentCacheMetadata({
          lastPage: newPg.last_page,
          totalRecords: newPg.total,
          pagesCached: Array.from(pagesCachedRef.current),
        })
      } else {
        const percentage = Math.round((1 / newPg.last_page) * 100)

        setCachePercentage(percentage)
        setIsCacheReady(false)
      }
    }
  }

  async function loadAndSetXeroContacts() {
    // Xero contacts are optional for the Orders list. Do not block order loading on Xero.
    setXeroContacts([])
  }

  async function getCachedResponse(url) {
    const persistentCached = await getFromIndexedDB(url)

    if (persistentCached) {
      return persistentCached
    }

    const cached = responseCacheRef.current.get(url)

    if (!cached) return null

    const isExpired = Date.now() - cached.createdAt > CACHE_TTL_MS

    if (isExpired) {
      responseCacheRef.current.delete(url)
      return null
    }

    return cached.data
  }

  function setCachedResponse(url, data) {
    responseCacheRef.current.set(url, {
      data,
      createdAt: Date.now(),
    })

    saveToIndexedDB(url, data).catch((err) => {
      console.error('[Orders Cache] Failed to save to IndexedDB:', err)
    })

    if (responseCacheRef.current.size > CACHE_MAX_ITEMS) {
      const firstKey = responseCacheRef.current.keys().next().value
      responseCacheRef.current.delete(firstKey)
    }
  }

  function updateCacheReadyState(totalPages = pg.last_page, totalRecords = pg.total) {
    const allPagesReady = totalPages > 0 && pagesCachedRef.current.size >= totalPages
    const allRecordsReady =
      Number(totalRecords) > 0 && allPagesDataRef.current.length >= Number(totalRecords)

    if (allPagesReady || allRecordsReady) {
      allPagesDataRef.current = sortOrdersByNumber(allPagesDataRef.current)
      cachedSearchResultsRef.current = [...allPagesDataRef.current]
      setCachePercentage(100)
      setIsCacheReady(true)

      savePersistentCacheMetadata({
        lastPage: totalPages,
        totalRecords: totalRecords,
        pagesCached: Array.from(pagesCachedRef.current),
      })

      return true
    }

    setIsCacheReady(false)
    return false
  }

  async function fetchPageWithRetry(page, totalPages, totalRecords, maxRetries = 3) {
    const url = buildApiUrl(page, { status: '', search: '' })

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.get(url, { timeout: 30000 })
        const data = response.data

        if (data.status === 200 && Array.isArray(data.invoice?.data)) {
          setCachedResponse(url, data)

          if (!pagesCachedRef.current.has(page)) {
            pagesCachedRef.current.add(page)

            const extractedData = data.invoice.data.map(extractDisplayFields)
            allPagesDataRef.current.push(...extractedData)
          }

          const percentage = Math.round((pagesCachedRef.current.size / totalPages) * 100)
          setCachePercentage(Math.min(percentage, 100))

          updateCacheReadyState(totalPages, totalRecords)

          return true
        }
      } catch (err) {
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 100))
        } else {
          console.debug(
            `Background cache failed for page ${page} after ${maxRetries} attempts:`,
            err?.message,
          )
        }
      }
    }

    return false
  }

  async function cacheAllPagesInBackground() {
    if (isCachingRef.current) return

    if (pg.last_page <= 1) {
      allPagesDataRef.current = sortOrdersByNumber(allPagesDataRef.current)
      cachedSearchResultsRef.current = [...allPagesDataRef.current]
      setCachePercentage(100)
      setIsCacheReady(true)

      savePersistentCacheMetadata({
        lastPage: pg.last_page,
        totalRecords: pg.total,
        pagesCached: Array.from(pagesCachedRef.current),
      })

      return
    }

    isCachingRef.current = true
    setIsCacheReady(false)

    try {
      pagesCachedRef.current.add(1)

      const pagesToCache = []

      for (let page = 2; page <= pg.last_page; page++) {
        if (!pagesCachedRef.current.has(page)) {
          pagesToCache.push(page)
        }
      }

      const promises = pagesToCache.map((page) =>
        fetchPageWithRetry(page, pg.last_page, pg.total, 3),
      )

      await Promise.allSettled(promises)

      updateCacheReadyState(pg.last_page, pg.total)
    } finally {
      isCachingRef.current = false
    }
  }

  function isCompletedOrder(row) {
    const orderStatus = toNumber(row?.order_status)
    const docketsComplete = toNumber(row?.dockets_complete)
    const tonsComplete = toNumber(row?.tons_complete)

    return orderStatus === 1 || (docketsComplete === 1 && tonsComplete === 1)
  }

  function matchesStatus(row, status) {
    const cleanStatus = String(status ?? '').trim()

    if (!cleanStatus) return true

    const orderStatus = toNumber(row?.order_status)
    const docketsReady = toNumber(row?.dockets_ready)
    const docketsComplete = toNumber(row?.dockets_complete)
    const tonsComplete = toNumber(row?.tons_complete)
    const isXero = toNumber(row?.is_xero)

    switch (cleanStatus) {
      case 'Completed':
      case 'Order Completed':
        return isCompletedOrder(row)

      case 'Dockets Complete':
        return docketsComplete === 1 && tonsComplete === 0 && orderStatus === 0

      case 'Tons Complete':
        return tonsComplete === 1 && docketsComplete === 0 && orderStatus === 0

      case 'Ready':
        return (
          docketsReady === 1 && docketsComplete === 0 && tonsComplete === 0 && orderStatus === 0
        )

      case 'Will Advise':
        return (
          docketsReady === 2 && docketsComplete === 0 && tonsComplete === 0 && orderStatus === 0
        )

      case 'In Progress':
      case 'New':
        return (
          docketsReady === 0 && docketsComplete === 0 && tonsComplete === 0 && orderStatus === 0
        )

      case 'Cancelled':
        return orderStatus === 3

      case 'Xero Synced':
        return isXero === 0 && orderStatus === 1

      case 'Xero Pending':
      case 'Xero Unsynced':
        return isCompletedOrder(row) && isXero === 1

      default:
        return true
    }
  }

  function filterOrdersFromClientMemory({ status = '', search = '' }) {
    if (!isCacheReady) return null

    const searchTokens = tokensFrom(search)

    return allPagesDataRef.current.filter((row) => {
      if (!matchesStatus(row, status)) {
        return false
      }

      if (searchTokens.length === 0) {
        return true
      }

      const hay = toSearchHaystack(row)

      return searchTokens.every((token) => hay.includes(token))
    })
  }

  function applyClientMemoryResults(results, page = 1) {
    const perPage = pg.per_page || 100
    const totalResults = results.length
    const totalPages = Math.max(1, Math.ceil(totalResults / perPage))

    const safePage = Math.min(Math.max(page, 1), totalPages)
    const startIdx = (safePage - 1) * perPage
    const endIdx = startIdx + perPage

    const sortedResults = sortOrdersByNumber(results)
    const paginatedResults = sortedResults.slice(startIdx, endIdx)

    setRows(paginatedResults)

    setPg((prev) => ({
      ...prev,
      current_page: safePage,
      last_page: totalPages,
      per_page: perPage,
      from: totalResults === 0 ? 0 : startIdx + 1,
      to: Math.min(endIdx, totalResults),
      total: totalResults,
    }))

    setLoading(false)
  }

  function reApplyCurrentClientView(page = pg.current_page) {
    if (!isCacheReady) return

    const nextResults = filterOrdersFromClientMemory({
      status: statusFilter,
      search: serverSearch,
    })

    if (nextResults !== null) {
      cachedSearchResultsRef.current = nextResults
      applyClientMemoryResults(nextResults, page)
    }
  }

  function patchCachedOrder(existingOrder, patch) {
    const merged = {
      ...existingOrder,
      ...patch,
      updated_at: patch?.updated_at || new Date().toISOString(),
    }

    if (patch?.customer) {
      merged.customer = {
        ...(existingOrder?.customer ?? {}),
        ...patch.customer,
      }
    }

    if (patch?.machine) {
      merged.machine = {
        ...(existingOrder?.machine ?? {}),
        ...patch.machine,
      }
    }

    if (patch?.orderdeliveryzone) {
      merged.orderdeliveryzone = {
        ...(existingOrder?.orderdeliveryzone ?? {}),
        ...patch.orderdeliveryzone,
      }
    }

    if (patch?.operator) {
      merged.operator = {
        ...(existingOrder?.operator ?? {}),
        ...patch.operator,
      }
    }

    if (Array.isArray(patch?.order_list)) {
      merged.order_list = patch.order_list
    }

    if (Array.isArray(patch?.order_operators)) {
      merged.order_operators = patch.order_operators
    }

    if (Array.isArray(patch?.operators)) {
      merged.operators = patch.operators
    }

    return extractDisplayFields(merged)
  }

  async function mutateOrderCache(mutation) {
    if (!mutation?.type || !mutation?.id) return

    const orderId = Number(mutation.id)

    if (mutation.type === 'delete') {
      allPagesDataRef.current = allPagesDataRef.current.filter(
        (item) => Number(item.id) !== orderId,
      )

      cachedSearchResultsRef.current = cachedSearchResultsRef.current
        ? cachedSearchResultsRef.current.filter((item) => Number(item.id) !== orderId)
        : null

      responseCacheRef.current.clear()

      await patchOrderInsideIndexedDBPages(orderId, () => null)

      setPg((prev) => ({
        ...prev,
        total: Math.max(0, Number(prev.total || 0) - 1),
      }))

      reApplyCurrentClientView()

      console.log(`[Orders Cache] ✅ Deleted order ${orderId} from memory and IndexedDB`)
      return
    }

    if (mutation.type === 'update') {
      const exists = allPagesDataRef.current.some((item) => Number(item.id) === orderId)

      let updatedOrder = null

      if (exists) {
        allPagesDataRef.current = allPagesDataRef.current.map((item) => {
          if (Number(item.id) !== orderId) return item

          if (mutation.order) {
            updatedOrder = extractDisplayFields(mutation.order)
            return updatedOrder
          }

          if (mutation.patch) {
            updatedOrder = patchCachedOrder(item, mutation.patch)
            return updatedOrder
          }

          updatedOrder = item
          return item
        })
      } else if (mutation.order || mutation.patch) {
        updatedOrder = mutation.order
          ? extractDisplayFields(mutation.order)
          : extractDisplayFields({
              id: mutation.id,
              ...(mutation.patch || {}),
            })

        allPagesDataRef.current = [updatedOrder, ...allPagesDataRef.current]
      }

      if (cachedSearchResultsRef.current) {
        cachedSearchResultsRef.current = cachedSearchResultsRef.current.map((item) => {
          if (Number(item.id) !== orderId) return item

          if (mutation.order) {
            return extractDisplayFields(mutation.order)
          }

          if (mutation.patch) {
            return patchCachedOrder(item, mutation.patch)
          }

          return item
        })
      }

      responseCacheRef.current.clear()

      if (updatedOrder) {
        await patchOrderInsideIndexedDBPages(orderId, (oldRow) => {
          if (mutation.order) {
            return extractDisplayFields(mutation.order)
          }

          if (mutation.patch) {
            return patchCachedOrder(oldRow, mutation.patch)
          }

          return oldRow
        })
      }

      reApplyCurrentClientView()

      console.log(`[Orders Cache] ✅ Updated order ${orderId} in memory and IndexedDB`)
      return
    }

    if (mutation.type === 'create') {
      const alreadyExists = allPagesDataRef.current.some((item) => Number(item.id) === orderId)

      if (!alreadyExists) {
        const createdOrder = mutation.order
          ? extractDisplayFields(mutation.order)
          : extractDisplayFields({
              id: mutation.id,
              ...(mutation.patch || {}),
            })

        allPagesDataRef.current = [createdOrder, ...allPagesDataRef.current]

        cachedSearchResultsRef.current = cachedSearchResultsRef.current
          ? [createdOrder, ...cachedSearchResultsRef.current]
          : null

        responseCacheRef.current.clear()

        await insertOrderIntoIndexedDBPageOne(createdOrder)

        setPg((prev) => ({
          ...prev,
          total: Number(prev.total || 0) + 1,
        }))

        reApplyCurrentClientView(1)

        console.log(`[Orders Cache] ✅ Created order ${orderId} in memory and IndexedDB`)
      }

      return
    }
  }

  async function fetchOrders({
    page = 1,
    status = statusFilter,
    search = '',
    forceRefresh = false,
  } = {}) {
    const reqId = ++reqSeqRef.current
    const url = buildApiUrl(page, { status, search })

    setLoading(true)

    try {
      if (!forceRefresh) {
        const cachedData = await getCachedResponse(url)

        if (cachedData) {
          if (reqId !== reqSeqRef.current) return

          if (cachedData.status !== 200) {
            throw new Error(cachedData.message || 'Unable to fetch orders')
          }

          applyOrdersResponse(cachedData)
          await loadAndSetXeroContacts()
          setLoading(false)
          return
        }
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      const { data } = await axios.get(url, {
        signal: controller.signal,
      })

      if (reqId !== reqSeqRef.current) return

      if (data.status !== 200) {
        throw new Error(data.message || 'Unable to fetch orders')
      }

      setCachedResponse(url, data)
      applyOrdersResponse(data)
      await loadAndSetXeroContacts()
    } catch (err) {
      const isCancelled =
        err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError'

      if (isCancelled) return

      if (reqId === reqSeqRef.current) {
        const cachedFallback = (await getCachedResponse(url)) || (await getStaleFromIndexedDB(url))

        if (cachedFallback?.status === 200) {
          applyOrdersResponse(cachedFallback)
          await loadAndSetXeroContacts()
          await swal.fire({
            icon: 'warning',
            title: 'Showing cached orders',
            text: 'The live orders service is unavailable. The displayed data may be out of date.',
            timer: 3500,
            showConfirmButton: false,
          })
          return
        }

        swal.fire({
          icon: 'error',
          title: 'Fetch Error',
          text: err?.response?.data?.message || err?.message || 'Unknown error',
        })
      }
    } finally {
      if (reqId === reqSeqRef.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    const initAndLoadCache = async () => {
      try {
        await initDB()

        const persistedMetadata = loadPersistentCacheMetadata()
        let shouldFetchFresh = true

        if (
          persistedMetadata &&
          persistedMetadata.lastPage > 0 &&
          persistedMetadata.pagesCached?.length
        ) {
          console.log('[Orders Cache] ✅ Cache is valid - restoring from IndexedDB')

          const cachedRecords = await loadCachedPagesFromIndexedDB(persistedMetadata.pagesCached)

          if (cachedRecords && cachedRecords.length > 0) {
            allPagesDataRef.current = cachedRecords
            pagesCachedRef.current = new Set(persistedMetadata.pagesCached || [])
            cachedSearchResultsRef.current = cachedRecords

            setCachePercentage(100)
            setIsCacheReady(true)

            applyClientMemoryResults(cachedRecords, 1)

            await loadAndSetXeroContacts()

            shouldFetchFresh = false
          }
        }

        if (shouldFetchFresh) {
          console.log('[Orders Cache] No valid cache found - fetching fresh data')

          cachedSearchResultsRef.current = null
          allPagesDataRef.current = []
          pagesCachedRef.current.clear()

          setIsCacheReady(false)
          setCachePercentage(0)

          fetchOrders({
            page: 1,
            status: '',
            search: '',
            forceRefresh: true,
          })
        }
      } catch (error) {
        console.error('[Orders Cache] Error during init and cache load:', error)

        fetchOrders({
          page: 1,
          status: '',
          search: '',
          forceRefresh: true,
        })
      }
    }

    initAndLoadCache()

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }

      if (cacheTimeoutRef.current) {
        clearTimeout(cacheTimeoutRef.current)
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (pg.current_page === 1 && !loading && pg.last_page > 0 && !isCacheReady) {
      if (cacheTimeoutRef.current) {
        clearTimeout(cacheTimeoutRef.current)
      }

      cacheTimeoutRef.current = setTimeout(() => {
        cacheAllPagesInBackground()
      }, BACKGROUND_CACHE_DELAY_MS)
    }

    return () => {
      if (cacheTimeoutRef.current) {
        clearTimeout(cacheTimeoutRef.current)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pg.current_page, loading, pg.last_page, pg.total, isCacheReady])

  /* ============================= Same Browser / Other Tab Sync ============================= */

  useEffect(() => {
    const tabId = getOrdersTabId()

    const applyIncomingMutation = (mutation) => {
      if (!mutation?.type || !mutation?.id) return

      // Skip only the exact same browser tab.
      // Do not skip by user_id because the same user can use another PC/browser.
      if (mutation.source_tab_id && mutation.source_tab_id === tabId) {
        return
      }

      mutateOrderCache(mutation).catch((error) => {
        console.debug('[Orders Sync] Failed to apply local mutation:', error)
      })
    }

    function handleOrdersCacheMutation(event) {
      if (event.key !== ORDERS_CACHE_MUTATION_KEY) return
      if (!event.newValue) return

      try {
        const mutation = JSON.parse(event.newValue)
        applyIncomingMutation(mutation)
      } catch (error) {
        console.debug('Invalid orders cache mutation:', error)
      }
    }

    window.addEventListener('storage', handleOrdersCacheMutation)

    let channel = null

    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel(ORDERS_CACHE_CHANNEL)

      channel.onmessage = (event) => {
        applyIncomingMutation(event.data)
      }
    }

    return () => {
      window.removeEventListener('storage', handleOrdersCacheMutation)

      if (channel) {
        channel.close()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCacheReady, statusFilter, serverSearch, pg.current_page])

  /* ============================= Xero Contacts Sync ============================= */

  useEffect(() => {
    const XERO_CONTACTS_MUTATION_KEY = 'xero_contacts_mutation'

    function handleXeroContactsMutation(event) {
      if (event.key !== XERO_CONTACTS_MUTATION_KEY) return
      if (!event.newValue) return

      try {
        console.log('[Xero Contacts] Received mutation from another tab')

        clearXeroContactsCache({ broadcast: false }).catch((err) => {
          console.debug('[Xero Contacts] Failed to clear cache:', err.message)
        })

        getCachedXeroContacts()
          .then((data) => {
            let formattedData = data

            if (data?.body?.body?.Contacts && !data?.body?.Contacts) {
              formattedData = data.body
            }

            setXeroContacts(formattedData ?? [])
          })
          .catch((err) => {
            console.debug('[Xero Contacts] Failed to refetch contacts:', err.message)
          })
      } catch (error) {
        console.debug('Invalid xero contacts mutation:', error)
      }
    }

    window.addEventListener('storage', handleXeroContactsMutation)

    return () => {
      window.removeEventListener('storage', handleXeroContactsMutation)
    }
  }, [])

  /* ============================= Cross-User / Different PC Sync with SSE ============================= */

  useEffect(() => {
    if (!isCacheReady) return

    const tabId = getOrdersTabId()
    let eventSource = null
    let heartbeatInterval = null
    let leaderCheckInterval = null
    let channel = null

    const applySsePayload = async (payload) => {
      if (!payload) return

      const version = Number(payload.version || 0)

      if (version && version <= lastAppliedSseVersionRef.current) {
        return
      }

      const mutations = Array.isArray(payload.mutations) ? payload.mutations : []

      for (const mutation of mutations) {
        if (!mutation?.type) continue

        if (mutation.type === 'xero_contacts_refresh') {
          clearXeroContactsCache({ broadcast: false }).catch(console.debug)

          getCachedXeroContacts()
            .then((data) => {
              const formatted =
                data?.body?.body?.Contacts && !data?.body?.Contacts ? data.body : data
              setXeroContacts(formatted ?? [])
            })
            .catch(console.debug)

          continue
        }

        if (!mutation.id) continue

        await mutateOrderCache(mutation)
      }

      if (version) {
        lastAppliedSseVersionRef.current = version
        localStorage.setItem(ORDERS_SSE_VERSION_KEY, String(version))
      }
    }

    const broadcastToOtherTabs = (payload) => {
      try {
        if (channel) {
          channel.postMessage(payload)
        }

        localStorage.setItem(
          ORDERS_SSE_MESSAGE_KEY,
          JSON.stringify({
            ...payload,
            timestamp: Date.now(),
          }),
        )
      } catch (error) {
        console.debug('[Orders SSE] Failed to broadcast payload to tabs:', error)
      }
    }

    const getLeader = () => {
      try {
        const raw = localStorage.getItem(ORDERS_SSE_LEADER_KEY)
        return raw ? JSON.parse(raw) : null
      } catch {
        return null
      }
    }

    const isCurrentLeader = () => {
      const leader = getLeader()

      return leader?.tabId === tabId && Number(leader?.expiresAt || 0) > Date.now()
    }

    const updateLeaderHeartbeat = () => {
      localStorage.setItem(
        ORDERS_SSE_LEADER_KEY,
        JSON.stringify({
          tabId,
          expiresAt: Date.now() + ORDERS_SSE_LEADER_TTL,
        }),
      )
    }

    const stopSse = () => {
      if (eventSource) {
        eventSource.close()
        eventSource = null
      }
    }

    const startSse = () => {
      if (eventSource) return

      const lastVersion = localStorage.getItem(ORDERS_SSE_VERSION_KEY) || '0'

      const sseUrl = buildSseUrl(lastVersion)

      console.log(
        `[Orders SSE] Opening EventSource from version ${lastVersion}:`,
        maskSseUrlForLog(sseUrl),
      )

      eventSource = new EventSource(sseUrl)

      eventSource.onopen = () => {
        console.log('[Orders SSE] Connected:', maskSseUrlForLog(sseUrl))
      }

      eventSource.addEventListener('orders.mutation', async (event) => {
        try {
          const payload = JSON.parse(event.data)

          await applySsePayload(payload)
          broadcastToOtherTabs(payload)
        } catch (error) {
          console.debug('[Orders SSE] Invalid mutation event:', error)
        }
      })

      eventSource.onerror = (error) => {
        console.debug('[Orders SSE] Connection interrupted:', error)
      }
    }

    const tryBecomeLeader = () => {
      const leader = getLeader()

      if (!leader || Number(leader.expiresAt || 0) <= Date.now()) {
        updateLeaderHeartbeat()

        setTimeout(() => {
          if (isCurrentLeader()) {
            startSse()
          }
        }, 200)

        return
      }

      if (leader.tabId === tabId) {
        updateLeaderHeartbeat()
        startSse()
        return
      }

      stopSse()
    }

    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel(ORDERS_SSE_CHANNEL)

      channel.onmessage = (event) => {
        applySsePayload(event.data).catch((error) => {
          console.debug('[Orders SSE] Failed to apply BroadcastChannel payload:', error)
        })
      }
    }

    const handleStorage = (event) => {
      if (event.key === ORDERS_SSE_MESSAGE_KEY && event.newValue) {
        try {
          const payload = JSON.parse(event.newValue)

          applySsePayload(payload).catch((error) => {
            console.debug('[Orders SSE] Failed to apply localStorage SSE payload:', error)
          })
        } catch (error) {
          console.debug('[Orders SSE] Invalid localStorage payload:', error)
        }
      }

      if (event.key === ORDERS_SSE_LEADER_KEY) {
        tryBecomeLeader()
      }
    }

    window.addEventListener('storage', handleStorage)

    heartbeatInterval = setInterval(() => {
      if (isCurrentLeader()) {
        updateLeaderHeartbeat()
        startSse()
      }
    }, 5000)

    leaderCheckInterval = setInterval(() => {
      tryBecomeLeader()
    }, 5000)

    tryBecomeLeader()

    return () => {
      stopSse()

      if (heartbeatInterval) clearInterval(heartbeatInterval)
      if (leaderCheckInterval) clearInterval(leaderCheckInterval)

      window.removeEventListener('storage', handleStorage)

      if (channel) {
        channel.close()
      }

      if (isCurrentLeader()) {
        localStorage.removeItem(ORDERS_SSE_LEADER_KEY)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCacheReady, statusFilter, serverSearch, pg.current_page])

  /* ============================= Handlers ============================= */

  function onStatusChange(e) {
    const nextStatus = e.target.value

    if (!isCacheReady) return

    setStatusFilter(nextStatus)

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }

    const clientResults = filterOrdersFromClientMemory({
      status: nextStatus,
      search: serverSearch,
    })

    if (clientResults !== null) {
      cachedSearchResultsRef.current = clientResults
      applyClientMemoryResults(clientResults, 1)
    }
  }

  function onServerSearchChange(next) {
    if (!isCacheReady) return

    const nextSearch = String(next ?? '')

    setServerSearch(nextSearch)
    setIsSearching(true)

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }

    searchDebounceRef.current = setTimeout(() => {
      const clientResults = filterOrdersFromClientMemory({
        status: statusFilter,
        search: nextSearch,
      })

      if (clientResults !== null) {
        cachedSearchResultsRef.current = clientResults
        applyClientMemoryResults(clientResults, 1)
      }

      setIsSearching(false)
    }, SEARCH_DEBOUNCE_MS)
  }

  function goToPage(nextPage) {
    const hasLast = Number.isFinite(pg.last_page) && pg.last_page > 0

    if (nextPage < 1 || (hasLast && nextPage > pg.last_page)) return

    if (cachedSearchResultsRef.current) {
      applyClientMemoryResults(cachedSearchResultsRef.current, nextPage)
      return
    }

    fetchOrders({
      page: nextPage,
      status: '',
      search: '',
    })
  }

  async function refreshCurrentPage() {
    responseCacheRef.current.clear()

    allPagesDataRef.current = []
    pagesCachedRef.current.clear()
    cachedSearchResultsRef.current = null

    localStorage.removeItem(ORDERS_CACHE_METADATA_KEY)
    await clearIndexedDBPages()

    setIsCacheReady(false)
    setCachePercentage(0)
    setStatusFilter('')
    setServerSearch('')
    setPageFilter('')

    fetchOrders({
      page: 1,
      status: '',
      search: '',
      forceRefresh: true,
    })
  }

  function clearPageFilter() {
    setPageFilter('')
  }

  /* ============================= Client-side Page Filter ============================= */

  const pageTokens = tokensFrom(pageFilter)

  const visibleRows = useMemo(() => {
    if (pageTokens.length === 0) return rows

    return rows.filter((r) => {
      const hay = toSearchHaystack(r)
      return pageTokens.every((t) => hay.includes(t))
    })
  }, [rows, pageTokens])

  /* ============================= Pagination Buttons ============================= */

  function paginationButtons() {
    const { current_page, last_page } = pg

    if (!Number.isFinite(last_page) || last_page <= 1) return [1]

    const around = [
      current_page - 2,
      current_page - 1,
      current_page,
      current_page + 1,
      current_page + 2,
    ].filter((n) => n > 1 && n < last_page)

    const unique = [...new Set([1, ...around, last_page])].sort((a, b) => a - b)
    const out = []

    for (let i = 0; i < unique.length; i++) {
      out.push(unique[i])

      if (i < unique.length - 1 && unique[i + 1] !== unique[i] + 1) {
        out.push('...')
      }
    }

    return out
  }

  function SkeletonTable() {
    return (
      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            {Array.from({ length: 13 }).map((_, j) => (
              <th key={j}>
                <Skeleton height={12} />
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {Array.from({ length: 10 }).map((_, i) => (
            <tr key={i}>
              {Array.from({ length: 13 }).map((_, j) => (
                <td key={j}>
                  <Skeleton height={12} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  const controlsDisabled = !isCacheReady

  /* ============================= Render ============================= */

  return (
    <div className="content-wrapper">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <NavLink to="/">Dashboard</NavLink>
          </li>

          <li className="breadcrumb-item active" aria-current="page">
            Orders
          </li>
        </ol>
      </nav>

      <h3 className="text-dark">Orders</h3>

      <div className="content-header">
        <div className="container">
          <div className="row mb-2">
            <div className="col-sm-6 p-0">
              {user.can('access orders') && (
                <div>
                  <NavLink
                    className="btn btn-primary mt-3 me-2"
                    to={{
                      pathname: '/orders/create',
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Add New Order
                  </NavLink>

                  <button
                    type="button"
                    className="btn btn-outline-secondary mt-3"
                    onClick={refreshCurrentPage}
                    disabled={loading}
                  >
                    Refresh
                  </button>
                </div>
              )}

              {!isCacheReady && cachePercentage > 0 && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  Preparing Quick Search: {cachePercentage}%
                </div>
              )}

              {isCacheReady && (
                <div style={{ fontSize: 12, color: '#28a745', marginTop: 6 }}>
                  ✓ Quick Search Ready
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title">Orders</h5>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <CFormSelect
                  size="sm"
                  className="w-auto"
                  value={statusFilter}
                  onChange={onStatusChange}
                  disabled={controlsDisabled}
                >
                  <option value="">All</option>
                  <option value="In Progress">New</option>
                  <option value="Ready">Ready</option>
                  <option value="Will Advise">Will Advise</option>
                  <option value="Tons Complete">Tons Complete</option>
                  <option value="Dockets Complete">Dockets Complete</option>
                  <option value="Completed">Order Completed</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Xero Synced">Sent to Xero</option>
                  <option value="Xero Unsynced">Not Sent to Xero</option>
                </CFormSelect>

                <input
                  type="search"
                  className="form-control form-control-sm"
                  style={{ width: 300 }}
                  placeholder={isCacheReady ? 'Filter page…' : 'Preparing quick search…'}
                  aria-label="Filter page"
                  value={pageFilter}
                  disabled={controlsDisabled}
                  onChange={(e) => setPageFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') clearPageFilter()
                  }}
                />

                {pageFilter ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={clearPageFilter}
                    disabled={controlsDisabled}
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              <div className="ms-auto" style={{ maxWidth: 300, width: '100%' }}>
                <SearchBar
                  onSearchChange={onServerSearchChange}
                  placeholder={isCacheReady ? 'Search…' : 'Preparing quick search…'}
                  disabled={controlsDisabled}
                  isSearching={isSearching}
                />
              </div>
            </div>
          </div>

          <div className="card-body" style={{ fontSize: 12, paddingTop: 5 }}>
            {loading ? (
              <SkeletonTable />
            ) : (
              <>
                <table
                  id="dataTable"
                  className="display table table-bordered table-striped"
                  style={{ width: '100%' }}
                >
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Code</th>
                      <th>Account Contact</th>
                      <th>Address Contact</th>
                      <th>Order Notes</th>
                      <th>Docket Numbers</th>
                      <th>Product(s)</th>
                      <th>T O Volume</th>
                      <th>Delivery Location</th>
                      <th>Machine</th>
                      <th>Status</th>
                      <th>Last Modified</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleRows.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center text-muted">
                          No matching records.
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((item, i) => (
                        <TableRow
                          key={item.id}
                          item={item}
                          index={i + 1 + (pg.current_page - 1) * pg.per_page}
                          xeroContacts={xeroContacts}
                        />
                      ))
                    )}
                  </tbody>
                </table>

                <PaginationBar
                  pagination={pg}
                  goToPage={goToPage}
                  getPaginationButtons={paginationButtons}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Open
